// src/components/LexChat.jsx
import React, { useState, useRef, useEffect } from 'react';
import { lexRuntime } from '../aws-config';
import { RecognizeTextCommand } from "@aws-sdk/client-lex-runtime-v2";
import '../ChatBot.css';

const LexChat = () => {
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [isConfigValid, setIsConfigValid] = useState(true);
  const [feedback, setFeedback] = useState({}); // Store feedback for each message
  const [lastUserQuestion, setLastUserQuestion] = useState(''); // Track user questions for feedback context
  const messagesEndRef = useRef(null);

  const userDetails = localStorage.getItem('userDetails');
  let firstName = 'Guest';

  if (userDetails) {
    try {
      const parsed = JSON.parse(userDetails);
      if (parsed.first_name) {
        firstName = parsed.first_name;
      }
    } catch (e) {
      console.error('Error parsing user_details:', e);
    }
  }

  useEffect(() => {
    // Validate required environment variables
    const requiredEnvVars = [
      'REACT_APP_LEX_BOT_ID',
      'REACT_APP_LEX_BOT_ALIAS_ID',
      'REACT_APP_IDENTITY_POOL_ID'
    ];

    const missingVars = requiredEnvVars.filter(varName => !process.env[varName]);

    if (missingVars.length > 0) {
      console.error('Missing required environment variables:', missingVars);
      setIsConfigValid(false);
      setMessages([
        {
          from: 'bot',
          text: 'Chat service is currently unavailable. Please check configuration.',
          timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        }
      ]);
    } else {
      setMessages([
        {
          from: 'bot',
          text: `Hi, ${firstName}! Welcome To Cartage Manager`,
          timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
          id: 'welcome-msg'
        }
      ]);
    }
  }, [firstName]);

  const sessionId = localStorage.getItem('user_id') || 'user-' + Date.now();

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Submit feedback to backend API (enhanced version)
  const submitFeedbackToAPI = async (messageId, feedbackType, messageText, userQuestion) => {
    try {
      const userId = localStorage.getItem('user_id');
      const feedbackData = {
        messageId: messageId,
        userId: userId,
        sessionId: sessionId,
        feedbackType: feedbackType === 'up' ? 'thumbs_up' : 'thumbs_down',
        userQuestion: userQuestion,
        botResponse: messageText,
        timestamp: new Date().toISOString()
      };

      console.log('Feedback Data to Submit:', feedbackData);

      // Store feedback locally as backup
      const localFeedback = JSON.parse(localStorage.getItem('chatbot_feedback') || '[]');
      localFeedback.push(feedbackData);
      localStorage.setItem('chatbot_feedback', JSON.stringify(localFeedback));

      // Try to submit to API (will work once CORS is fixed)
      try {
        const FEEDBACK_URL = process.env.REACT_APP_FEEDBACK_API_URL || 'https://unv70mu9ji.execute-api.us-east-1.amazonaws.com/default/LambdaforChatbotFeedback';
        const response = await fetch(FEEDBACK_URL, {
          method: 'POST',
          mode: 'no-cors',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(feedbackData)
        });

        console.log('Feedback sent to API successfully');
        return true;
      } catch (apiError) {
        console.log('API submission failed, but feedback saved locally');
        return true; // Still return true since we saved locally
      }
    } catch (error) {
      console.error('Error submitting feedback:', error);
      return false;
    }
  };

  // Handle feedback for bot messages (enhanced version)
  const handleFeedback = async (messageId, type) => {
    // Check if feedback already exists for this message (lock mechanism)
    if (feedback[messageId]) {
      console.log('Feedback already submitted for this message');
      return;
    }

    // Set feedback state immediately (this locks the selection)
    setFeedback(prev => ({
      ...prev,
      [messageId]: type
    }));

    // Find the message text and associated user question for API submission
    const message = messages.find(msg => msg.id === messageId);
    const messageText = message ? message.text : '';

    // Submit feedback to backend API
    const success = await submitFeedbackToAPI(messageId, type, messageText, lastUserQuestion);

    if (success) {
      console.log(`Feedback successfully submitted for message ${messageId}: ${type}`);
    } else {
      // If API call failed, revert the feedback state
      setFeedback(prev => {
        const newFeedback = { ...prev };
        delete newFeedback[messageId];
        return newFeedback;
      });
      console.error('Failed to submit feedback, reverted state');
    }
  };

  const sendMessage = async () => {
    if (!input.trim() || !isConfigValid) return;

    const userQuestion = input.trim();
    // Store user question for feedback context
    setLastUserQuestion(userQuestion);

    const userMessage = {
      from: 'user',
      text: userQuestion,
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      id: `user-${Date.now()}`
    };
    setMessages((prev) => [...prev, userMessage]);
    setInput('');
    setIsTyping(true);

    const params = {
      botAliasId: process.env.REACT_APP_LEX_BOT_ALIAS_ID,
      botId: process.env.REACT_APP_LEX_BOT_ID,
      localeId: process.env.REACT_APP_LEX_LOCALE_ID || "en_US",
      sessionId,
      text: userQuestion,
      sessionState: {
        sessionAttributes: {
          userId: sessionId,
          first_name: firstName
        }
      }
    };

    try {
      const command = new RecognizeTextCommand(params);
      const response = await lexRuntime.send(command);
      const botResponse = response.messages?.[0]?.content || 'I apologize, but I didn\'t receive a proper response. Please try again.';

      setTimeout(() => {
        const messageId = `bot-${Date.now()}`;
        setMessages(prev => [...prev, {
          from: 'bot',
          text: botResponse,
          timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
          id: messageId
        }]);
        setIsTyping(false);
      }, 1000);
    } catch (error) {
      console.error('Lex Error:', error);
      let errorMessage = 'I\'m having trouble connecting right now. Please try again in a moment.';

      // Provide more specific error messages
      if (error.name === 'ValidationException') {
        errorMessage = 'There was a validation error. Please check your message and try again.';
      } else if (error.name === 'ThrottlingException') {
        errorMessage = 'Too many requests. Please wait a moment and try again.';
      } else if (error.name === 'AccessDeniedException') {
        errorMessage = 'Access denied. Please check your permissions.';
      }

      setTimeout(() => {
        const messageId = `bot-error-${Date.now()}`;
        setMessages(prev => [...prev, {
          from: 'bot',
          text: errorMessage,
          timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
          id: messageId
        }]);
        setIsTyping(false);
      }, 1000);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const toggleChat = () => setIsOpen(!isOpen);

  // Helper function to view stored feedback (for debugging)
  const viewStoredFeedback = () => {
    const feedback = JSON.parse(localStorage.getItem('chatbot_feedback') || '[]');
    console.log('📊 All Stored Feedback:', feedback);
    return feedback;
  };

  // Add to window for easy access in console (development only)
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      window.viewChatFeedback = viewStoredFeedback;
      console.log('Tip: Type "viewChatFeedback()" in console to see all stored feedback');
    }
  }, []);

  return (
    <div className="chat-bot-container">
      <div className={`chat-window ${isOpen ? 'open' : ''}`}>
        <div className="chat-header">
          <h3>Cartage Manager</h3>
          <button className="close-btn" onClick={toggleChat}>×</button>
        </div>

        <div className="messages-container">
          {messages.map((msg, i) => (
            <div key={i} className={`message ${msg.from}`}>
              <div className="message-bubble">
                {msg.text}
                <div className="message-time">{msg.timestamp}</div>
                {msg.from === 'bot' && msg.id && (
                  <div className="feedback-container">
                    <button
                      className={`feedback-btn thumbs-up ${feedback[msg.id] === 'up' ? 'active' : ''} ${feedback[msg.id] ? 'locked' : ''}`}
                      onClick={() => handleFeedback(msg.id, 'up')}
                      disabled={feedback[msg.id] !== undefined}
                      title={feedback[msg.id] ? 'Feedback submitted' : 'Good response'}
                    >
                      <svg viewBox="0 0 24 24">
                        <path d="M23,10C23,8.89 22.1,8 21,8H14.68L15.64,3.43C15.66,3.33 15.67,3.22 15.67,3.11C15.67,2.7 15.5,2.32 15.23,2.05L14.17,1L7.59,7.58C7.22,7.95 7,8.45 7,9V19A2,2 0 0,0 9,21H18C18.83,21 19.54,20.5 19.84,19.78L22.86,12.73C22.95,12.5 23,12.26 23,12V10.08L23,10M1,21H5V9H1V21Z" />
                      </svg>
                    </button>
                    <button
                      className={`feedback-btn thumbs-down ${feedback[msg.id] === 'down' ? 'active' : ''} ${feedback[msg.id] ? 'locked' : ''}`}
                      onClick={() => handleFeedback(msg.id, 'down')}
                      disabled={feedback[msg.id] !== undefined}
                      title={feedback[msg.id] ? 'Feedback submitted' : 'Poor response'}
                    >
                      <svg viewBox="0 0 24 24">
                        <path d="M19,15H23V3H19V15M15,3H6C5.17,3 4.46,3.5 4.16,4.22L1.14,11.27C1.05,11.5 1,11.74 1,12V14A2,2 0 0,0 3,16H9.31L8.36,20.57C8.34,20.67 8.33,20.78 8.33,20.89C8.33,21.3 8.5,21.67 8.77,21.94L9.83,23L16.41,16.41C16.78,16.05 17,15.55 17,15V5A2,2 0 0,0 15,3Z" />
                      </svg>
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
          {isTyping && (
            <div className="message bot">
              <div className="typing-indicator">
                <div className="typing-dots">
                  <div className="typing-dot"></div>
                  <div className="typing-dot"></div>
                  <div className="typing-dot"></div>
                </div>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        <div className="lex-input-container">
          <textarea
            className="lex-message-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Type a message..."
            rows="1"
          />
          <button className="send-btn" onClick={sendMessage} disabled={!input.trim()}>
            <svg viewBox="0 0 24 24">
              <path d="M2,21L23,12L2,3V10L17,12L2,14V21Z" />
            </svg>
          </button>
        </div>
      </div>

      <button className="chat-icon" onClick={toggleChat}>
        {isOpen ? (
          <svg viewBox="0 0 24 24">
            <path d="M7 10l5 5 5-5H7z" />
          </svg>
        ) : (
          <svg viewBox="0 0 24 24">
            <path d="M20,2H4A2,2 0 0,0 2,4V22L6,18H20A2,2 0 0,0 22,16V4C22,2.89 21.1,2 20,2M6,9V7H18V9H6M14,11V13H6V11H14M16,15V17H6V15H16Z" />
          </svg>
        )}
      </button>
    </div>
  );
};

export default LexChat;
